/**
 * Every routed address, grouped by who may reach it.
 *
 * Plain data on purpose: no React and no MUI, so a Playwright test can import
 * it under Node as easily as the app does. The navigation adds its icons to
 * the entries here, the route table builds its guards from them, and the
 * browser tests sweep them - so a page routed tomorrow is in every sweep
 * without anybody adding it to a list, and no list is kept twice.
 *
 * Relative imports rather than the '@/' alias, because the browser tests read
 * this file without the app's path mapping.
 */
import { ROLES, type Role } from '../auth/roles';

import { ROUTES } from './routes';

/**
 * Any role that belongs to an organization.
 *
 * Written as Admin alone because a Super Admin satisfies a check for Admin -
 * the requirements say a Super Admin inherits every Admin capability, and the
 * role model implements that. Activist staff are not an organization role at
 * all, so they do not pass it.
 */
export const ANY_ORG_ROLE: readonly Role[] = [ROLES.ADMIN];

/** The organization's own Super Admin, and nobody else. */
export const SUPER_ADMIN_ONLY: readonly Role[] = [ROLES.SUPER_ADMIN];

/** Activist Technologies staff, who belong to no organization. */
export const STAFF_ONLY: readonly Role[] = [ROLES.ACTIVIST_STAFF];

export interface PathEntry {
  readonly label: string;
  readonly path: string;
  /**
   * Which roles may use this entry. A sub-page or action that omits it takes
   * the roles of the section it sits under, so the common case states nothing
   * and the exceptions are visible.
   */
  readonly allow?: readonly Role[];
}

export interface SectionEntry {
  readonly label: string;
  /** Where the section itself goes, and what marks it as current. */
  readonly path: string;
  readonly children?: readonly PathEntry[];
  /** The "+ New ..." button the wireframes show inside the open section. */
  readonly action?: PathEntry;
  /**
   * Which roles may use this section. Its children and its action inherit this
   * unless they state their own. Every section declares one; a test fails if a
   * new section is added without deciding, rather than leaving it open.
   */
  readonly allow: readonly Role[];
  /**
   * One line saying what the section does, from the wireframes. Shown on the
   * section's placeholder page, and matched and shown by the top bar's search,
   * so both read the same words from here.
   */
  readonly hint?: string;
}

/**
 * The twelve sections of the portal, taken from the wireframes: the ten of
 * September, and Petitions and Events, which the October wireframes added
 * after Campaigns.
 *
 * Sections arrive over five months, so adding one should mean editing this
 * list, not hunting through the navigation component or the route table.
 */
export const CONTENT_SECTIONS = [
  {
    label: 'Dashboard',
    path: ROUTES.dashboard,
    allow: ANY_ORG_ROLE,
    hint: 'Organization activity and performance at a glance.',
  },
  {
    label: 'Notifications',
    path: ROUTES.notifications.root,
    allow: ANY_ORG_ROLE,
    hint: 'Create, schedule and review push notifications sent to supporters.',
    children: [
      { label: 'All Notifications', path: ROUTES.notifications.all },
      { label: 'Drafts', path: ROUTES.notifications.drafts },
      { label: 'Scheduled', path: ROUTES.notifications.scheduled },
      { label: 'Sent', path: ROUTES.notifications.sent },
    ],
    action: { label: 'New Notification', path: ROUTES.notifications.new },
  },
  {
    label: 'Manage ARC',
    path: ROUTES.arc.root,
    allow: ANY_ORG_ROLE,
    hint: 'Arrange standard and custom sections in the order supporters will see them.',
    children: [
      { label: 'ARC Builder', path: ROUTES.arc.builder },
      { label: 'Volunteer Recruitment', path: ROUTES.arc.volunteer },
      { label: 'Fundraising', path: ROUTES.arc.fundraising },
    ],
  },
  {
    label: 'Campaigns',
    path: ROUTES.campaigns.root,
    allow: ANY_ORG_ROLE,
    hint: 'Create and manage calls to action that connect supporters with their elected officials.',
    children: [
      { label: 'All Campaigns', path: ROUTES.campaigns.all },
      { label: 'Active', path: ROUTES.campaigns.active },
      { label: 'Inactive', path: ROUTES.campaigns.inactive },
    ],
    action: { label: 'New Campaign', path: ROUTES.campaigns.new },
  },
  {
    label: 'Petitions',
    path: ROUTES.petitions.root,
    allow: ANY_ORG_ROLE,
    hint: 'Create petitions and track verified signatures collected through ActivistARC.',
    children: [
      { label: 'All Petitions', path: ROUTES.petitions.all },
      { label: 'Signatures', path: ROUTES.petitions.signatures },
      { label: 'Archived Petitions', path: ROUTES.petitions.archived },
    ],
    action: { label: 'New Petition', path: ROUTES.petitions.new },
  },
  {
    label: 'Events',
    path: ROUTES.events.root,
    allow: ANY_ORG_ROLE,
    hint: 'Create and manage events published by your organization.',
    children: [
      { label: 'All Events', path: ROUTES.events.all },
      { label: 'Upcoming Events', path: ROUTES.events.upcoming },
      { label: 'Past Events', path: ROUTES.events.past },
    ],
    action: { label: 'New Event', path: ROUTES.events.new },
  },
  {
    label: 'Guides',
    path: ROUTES.guides.root,
    allow: ANY_ORG_ROLE,
    hint: "Manage voter guides personalized to each supporter's address and ballot.",
    children: [
      { label: 'Current Guides', path: ROUTES.guides.current },
      { label: 'Endorsements', path: ROUTES.guides.endorsements },
      { label: 'Archived Guides', path: ROUTES.guides.archived },
    ],
    action: { label: 'New Guide', path: ROUTES.guides.new },
  },
  {
    label: 'Surveys',
    path: ROUTES.surveys.root,
    allow: ANY_ORG_ROLE,
    hint: 'Create surveys and collect feedback from the public or your supporters.',
    children: [
      { label: 'All Surveys', path: ROUTES.surveys.all },
      { label: 'Responses', path: ROUTES.surveys.responses },
      { label: 'Archived Surveys', path: ROUTES.surveys.archived },
    ],
    action: { label: 'New Survey', path: ROUTES.surveys.new },
  },
  {
    label: 'Pages',
    path: ROUTES.pages.root,
    allow: ANY_ORG_ROLE,
    hint: 'Create and manage informational pages published by your organization.',
    children: [
      { label: 'Custom Pages', path: ROUTES.pages.custom },
      { label: 'ActivistARC Pages', path: ROUTES.pages.activistArc },
    ],
    action: { label: 'New Custom Page', path: ROUTES.pages.new },
  },
  {
    label: 'Documents',
    path: ROUTES.documents.root,
    allow: ANY_ORG_ROLE,
    hint: 'Upload, organize and manage PDFs published by your organization.',
    children: [
      { label: 'Document Library', path: ROUTES.documents.library },
      { label: 'Categories', path: ROUTES.documents.categories },
    ],
    action: { label: 'Upload Document', path: ROUTES.documents.upload },
  },
  {
    label: 'Supporters',
    path: ROUTES.supporters.root,
    allow: ANY_ORG_ROLE,
    hint: 'View people who follow your organization and manage supporter invitations.',
    children: [
      { label: 'All Supporters', path: ROUTES.supporters.all },
      { label: 'Invitations', path: ROUTES.supporters.invitations },
    ],
    action: { label: 'Invite Supporters', path: ROUTES.supporters.invite },
  },
  {
    label: 'Help',
    path: ROUTES.help,
    allow: ANY_ORG_ROLE,
    hint: 'Search setup instructions, feature guides and answers.',
  },
] as const satisfies readonly SectionEntry[];

/**
 * The Activist staff area's own navigation.
 *
 * A separate list because staff are not an organization role at all - they
 * manage accounts rather than belonging to one.
 */
export const STAFF_SECTION_ENTRIES = [
  { label: 'Organizations', path: ROUTES.staff.organizations, allow: STAFF_ONLY },
  { label: 'Activity', path: ROUTES.staff.activity, allow: STAFF_ONLY },
] as const satisfies readonly SectionEntry[];

/**
 * The account rail, from the wireframes. Log Out is the sixth entry and lives
 * in the rail and the menu themselves, because it ends a session rather than
 * leading to a page.
 *
 * Every entry here belongs to the organization's Super Admin. Each is the
 * subject of a Super Admin story - Profile is US-020, Users is US-021, Billing
 * is US-022 - and the delivery plan says an Admin sees neither Billing nor
 * Account.
 *
 * The roles live beside the item rather than in the router, so the menu and the
 * route guard read one list and cannot drift into disagreeing.
 */
export const ACCOUNT_ENTRIES = [
  { label: 'Profile', path: ROUTES.account.profile, allow: SUPER_ADMIN_ONLY },
  { label: 'Account', path: ROUTES.account.account, allow: SUPER_ADMIN_ONLY },
  { label: 'Users', path: ROUTES.account.users, allow: SUPER_ADMIN_ONLY },
  { label: 'Billing', path: ROUTES.account.billing, allow: SUPER_ADMIN_ONLY },
  { label: 'Activity', path: ROUTES.account.activity, allow: SUPER_ADMIN_ONLY },
] as const satisfies readonly PathEntry[];

/** The same address once, in the order first seen. */
function unique(paths: readonly string[]): string[] {
  return [...new Set(paths)];
}

/**
 * Every address one section list reaches: each section, its sub-pages and its
 * action. A section's first sub-page is usually the section's own address, so
 * repeats are dropped.
 */
function sectionPaths(sections: readonly SectionEntry[]): string[] {
  return unique(
    sections.flatMap((section) => [
      section.path,
      ...(section.children ?? []).map((child) => child.path),
      ...(section.action ? [section.action.path] : []),
    ]),
  );
}

/**
 * The routed addresses, by who may reach them.
 *
 * An address with a placeholder (`:id`, `:token`) is the route pattern itself;
 * a sweep that opens it has to supply a real value.
 */
export const PATHS = {
  /** Open to anybody. Signing in happens outside the portal frame. */
  signedOut: [
    ROUTES.signIn,
    ROUTES.forgotPassword,
    ROUTES.resetPassword,
    ROUTES.invitation(':token'),
  ],
  /**
   * Any organization role: the twelve content sections and their sub-pages,
   * and the record addresses inside them that no navigation lists.
   */
  content: [...sectionPaths(CONTENT_SECTIONS), ROUTES.pages.edit(':id')],
  /** The account rail: the organization's Super Admin. */
  account: ACCOUNT_ENTRIES.map((entry) => entry.path),
  /** Activist staff. */
  staff: [
    ROUTES.staff.organizations,
    ROUTES.staff.newOrganization,
    ROUTES.staff.organization(':id'),
    ROUTES.staff.activity,
  ],
  /**
   * Anybody signed in: the front page, which redirects each role to where it
   * belongs, the refusal page, and the shared-components reference - which is
   * listed here and nowhere else, so every sweep covers it and no navigation,
   * menu or search ever offers it.
   */
  shell: [ROUTES.home, ROUTES.forbidden, ROUTES.components],
} as const satisfies Record<string, readonly string[]>;

/** Every routed address, once. */
export const ALL_PATHS: readonly string[] = unique(Object.values(PATHS).flat());
